/**
 * 1.8 – AI Research: Truy xuất Pháp luật & Án lệ
 * 2 cột: Sidebar Kho Luật (240px) | Kết quả (Văn bản QPPL + Án lệ). Thanh tìm kiếm toàn chiều rộng. Chatbot floating.
 */
import { useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  LinearProgress,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  Snackbar,
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import { Search, ExpandMore, MenuBook, Gavel, Close, Send, Chat as ChatIcon, SmartToy } from '@mui/icons-material'

const LAW_SECTIONS = [
  { title: 'Luật Hôn nhân và Gia đình', items: ['Luật HN&GĐ 2014 (Điều 48)', 'Luật HN&GĐ 2014 (Điều 56)', 'Luật HN&GĐ 2015 (Điều A)'] },
  { title: 'Luật Đất đai', items: ['Luật Đất đai 2013 (Điều 100)'] },
  { title: 'Án lệ Hội đồng Thẩm phán', items: ['Án lệ số 03/2016', 'Án lệ số 09/2018'] },
]

const ALL_LAW_RESULTS = [
  { id: '1', title: 'Bộ luật Dân sự 2015 (Điều 429)', relevance: 92, excerpt: 'Luật HN&GĐ 2014, Điều 56: quy định về tranh chấp tài sản chung...', full: 'Điều 429. Tranh chấp về quyền sở hữu, quyền khác đối với tài sản...', category: 'dansu' },
  { id: '2', title: 'Luật HN&GĐ 2014 (Điều 48)', relevance: 88, excerpt: 'Điều kiện ly hôn và phân chia tài sản...', full: 'Điều 48. Ly hôn theo yêu cầu của một bên khi có căn cứ vợ hoặc chồng có hành vi bạo lực...', category: 'hn' },
  { id: '3', title: 'Luật HN&GĐ 2014 (Điều 56)', relevance: 85, excerpt: 'Chế độ tài sản của vợ chồng...', full: 'Điều 56. Tài sản chung của vợ chồng...', category: 'hn' },
  { id: '4', title: 'Luật Đất đai 2013 (Điều 100)', relevance: 78, excerpt: 'Thủ tục cấp GCN QSDĐ...', full: 'Điều 100. Giấy chứng nhận quyền sử dụng đất...', category: 'datdai' },
]

const ALL_CASE_LAW = [
  { id: '1', code: 'Án lệ số 12/2023/HĐTM-PT', summary: 'Tranh chấp tài sản sau ly hôn, tài sản hình thành trước hôn nhân...', result: 'Bác yêu cầu của nguyên đơn', full: 'Nội dung án lệ đầy đủ (demo).' },
  { id: '2', code: 'Án lệ số 03/2016', summary: 'Giải quyết tranh chấp ly hôn khi có con nhỏ.', result: 'Chấp nhận một phần yêu cầu.', full: 'Nội dung án lệ 03/2016 (demo).' },
  { id: '3', code: 'Án lệ số 09/2018', summary: 'Phân chia tài sản chung khi ly hôn.', result: 'Căn cứ Điều 59 Luật HN&GĐ 2014.', full: 'Nội dung án lệ 09/2018 (demo).' },
]

function AIResearchPage() {
  const theme = useTheme()
  const [chatOpen, setChatOpen] = useState(false)
  const [query, setQuery] = useState('Tranh chấp ly hôn và phân chia tài sản chung')
  const [searchDone, setSearchDone] = useState(true)
  const [selectedLawItem, setSelectedLawItem] = useState<string | null>(null)
  const [lawResults] = useState(ALL_LAW_RESULTS)
  const [caseResults] = useState(ALL_CASE_LAW)
  const [viewLawId, setViewLawId] = useState<string | null>(null)
  const [viewCaseId, setViewCaseId] = useState<string | null>(null)
  const [compareCaseId, setCompareCaseId] = useState<string | null>(null)
  const [chatMessages, setChatMessages] = useState<{ role: 'user' | 'bot'; text: string }[]>([
    { role: 'bot', text: 'Tôi đã tìm thấy 8 văn bản liên quan. Bạn có muốn tôi phân tích điểm khác biệt giữa Án lệ 12 và vụ án này không?' },
  ])
  const [chatInput, setChatInput] = useState('')
  const [snack, setSnack] = useState('')

  const isCaseLawSelection = selectedLawItem?.includes('Án lệ') ?? false
  const displayedLawResults =
    !selectedLawItem || isCaseLawSelection
      ? lawResults
      : lawResults.filter((r) => r.title.includes(selectedLawItem) || selectedLawItem.includes(r.title))
  const displayedCaseResults =
    !selectedLawItem
      ? caseResults
      : isCaseLawSelection
        ? caseResults.filter((c) => c.code === selectedLawItem)
        : caseResults

  const handleSearch = () => {
    setSearchDone(false)
    setSnack('Đang tra cứu pháp luật và án lệ...')
    setTimeout(() => {
      setSearchDone(true)
      setSnack('Đã tìm thấy ' + lawResults.length + ' văn bản QPPL và ' + caseResults.length + ' án lệ.')
    }, 800)
  }

  const handleSendChat = () => {
    if (!chatInput.trim()) return
    setChatMessages((prev) => [...prev, { role: 'user', text: chatInput }])
    setChatInput('')
    setTimeout(() => {
      setChatMessages((prev) => [
        ...prev,
        { role: 'bot', text: 'Tôi đã ghi nhận câu hỏi. (Demo: phản hồi AI sẽ hiển thị khi tích hợp backend.)' },
      ])
    }, 500)
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 112px)', overflow: 'hidden' }}>
      <Paper elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', px: 2, py: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          UEL E-Court – Hệ thống Trợ lý AI Án lệ & Pháp luật
        </Typography>
      </Paper>

      <Box sx={{ px: 2, py: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
        <TextField
          fullWidth
          placeholder="Nhập tình huống pháp lý hoặc từ khóa... (VD: Tranh chấp đất đai, hôn nhân, không có hợp đồng...)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
          size="medium"
          sx={{ maxWidth: 560, flex: '1 1 300px' }}
        />
        <Button variant="contained" onClick={handleSearch} disabled={!searchDone}>Phân tích</Button>
      </Box>

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Sidebar – Kho Luật (240px) */}
        <Paper elevation={0} sx={{ width: 240, borderRight: 1, borderColor: 'divider', overflow: 'auto', flexShrink: 0 }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Kho Luật tham chiếu</Typography>
            {selectedLawItem && (
              <Button size="small" onClick={() => setSelectedLawItem(null)} sx={{ mb: 1 }}>Bỏ lọc</Button>
            )}
            {LAW_SECTIONS.map((sec, i) => (
              <Accordion key={i} disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="body2">{sec.title}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  {sec.items.map((item, j) => (
                    <Box
                      key={j}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        py: 0.25,
                        cursor: 'pointer',
                        bgcolor: selectedLawItem === item ? 'action.selected' : 'transparent',
                        borderRadius: 1,
                        px: 0.5,
                      }}
                      onClick={() => setSelectedLawItem(selectedLawItem === item ? null : item)}
                    >
                      <MenuBook sx={{ fontSize: 18, color: 'primary.main' }} />
                      <Typography variant="body2">{item}</Typography>
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>
            ))}
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">Án lệ</Typography>
              {LAW_SECTIONS[2].items.map((item, j) => (
                <Box
                  key={j}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    py: 0.25,
                    cursor: 'pointer',
                    bgcolor: selectedLawItem === item ? 'action.selected' : 'transparent',
                    borderRadius: 1,
                    px: 0.5,
                  }}
                  onClick={() => setSelectedLawItem(selectedLawItem === item ? null : item)}
                >
                  <Gavel sx={{ fontSize: 18, color: 'secondary.main' }} />
                  <Typography variant="body2">{item}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>

        {/* Vùng kết quả – 2 cột */}
        <Box sx={{ flex: 1, display: 'flex', gap: 2, p: 2, overflow: 'auto', minWidth: 0 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Văn bản Quy phạm Pháp luật liên quan</Typography>
            {!searchDone && <LinearProgress sx={{ mb: 1 }} />}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {displayedLawResults.map((r) => (
                <Card key={r.id} variant="outlined">
                  <CardContent sx={{ py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <MenuBook color="primary" fontSize="small" />
                      <Typography variant="body2" fontWeight={600}>{r.title}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">Độ phù hợp: </Typography>
                    <LinearProgress variant="determinate" value={r.relevance} color="primary" sx={{ height: 6, borderRadius: 1, mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">{r.excerpt}</Typography>
                    <Button size="small" sx={{ mt: 1 }} onClick={() => setViewLawId(r.id)}>Xem toàn bộ</Button>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Án lệ & Bản án tương tự</Typography>
            {!searchDone && <LinearProgress sx={{ mb: 1 }} />}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {displayedCaseResults.map((r) => (
                <Card key={r.id} variant="outlined">
                  <CardContent sx={{ py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <Gavel color="secondary" fontSize="small" />
                      <Typography variant="body2" fontWeight={600}>{r.code}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">Tóm tắt: {r.summary}</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>Kết quả: {r.result}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Button size="small" onClick={() => setViewCaseId(r.id)}>Xem chi tiết</Button>
                      <Button size="small" variant="outlined" onClick={() => setCompareCaseId(r.id)}>So sánh với vụ này</Button>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Floating Chatbot – Trợ lý AI Research (sát góc dưới bên phải) */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          right: 0,
          left: 'auto',
          top: 'auto',
          zIndex: 1300,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          justifyContent: 'flex-end',
          m: 0,
          p: 0,
        }}
      >
        <Collapse in={chatOpen} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', width: 380 }}>
            <Paper
              elevation={8}
              sx={{
                width: 380,
                height: 520,
                maxHeight: 'calc(100vh - 0px)',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '16px 16px 0 0',
                overflow: 'hidden',
                border: 1,
                borderColor: alpha(theme.palette.primary.main, 0.12),
                borderBottom: 'none',
                boxShadow: '0 -4px 24px rgba(0,0,0,0.12)',
              }}
            >
              <Box
                sx={{
                  py: 1.5,
                  px: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  color: 'primary.contrastText',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: alpha('#fff', 0.2), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <SmartToy sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>Trợ lý AI Research</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>Pháp luật & Án lệ</Typography>
                  </Box>
                </Box>
                <IconButton size="small" sx={{ color: 'inherit' }} onClick={() => setChatOpen(false)} aria-label="Đóng"><Close /></IconButton>
              </Box>
              <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: alpha(theme.palette.grey[500], 0.04), display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {chatMessages.map((msg, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex',
                      justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                      alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                      maxWidth: '88%',
                    }}
                  >
                    {msg.role === 'bot' ? (
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexDirection: 'row' }}>
                        <Box sx={{ mt: 0.5, width: 28, height: 28, borderRadius: '50%', bgcolor: alpha(theme.palette.primary.main, 0.14), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <SmartToy sx={{ fontSize: 16, color: 'primary.main' }} />
                        </Box>
                        <Paper
                          elevation={0}
                          sx={{
                            px: 2,
                            py: 1.5,
                            borderRadius: 2,
                            borderTopLeftRadius: 4,
                            bgcolor: 'background.paper',
                            border: 1,
                            borderColor: alpha(theme.palette.primary.main, 0.12),
                            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                          }}
                        >
                          <Typography variant="body2" sx={{ lineHeight: 1.6, color: 'text.primary' }}>{msg.text}</Typography>
                        </Paper>
                      </Box>
                    ) : (
                      <Paper
                        elevation={0}
                        sx={{
                          px: 2,
                          py: 1.5,
                          borderRadius: 2,
                          borderTopRightRadius: 4,
                          bgcolor: theme.palette.primary.main,
                          color: 'primary.contrastText',
                          boxShadow: '0 2px 8px rgba(37, 99, 235, 0.25)',
                        }}
                      >
                        <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{msg.text}</Typography>
                      </Paper>
                    )}
                  </Box>
                ))}
              </Box>
              <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper', display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Hỏi thêm về pháp luật, án lệ..."
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendChat()}
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: alpha(theme.palette.grey[500], 0.06) },
                  }}
                />
                <IconButton
                  onClick={handleSendChat}
                  disabled={!chatInput.trim()}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    '&:hover': { bgcolor: 'primary.dark' },
                    '&.Mui-disabled': { bgcolor: alpha(theme.palette.primary.main, 0.4), color: 'white' },
                  }}
                  aria-label="Gửi"
                >
                  <Send fontSize="small" />
                </IconButton>
              </Box>
            </Paper>
          </Box>
        </Collapse>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            justifyContent: 'flex-end',
          }}
        >
          {!chatOpen && (
            <IconButton
              onClick={() => setChatOpen(true)}
              sx={{
                width: 56,
                height: 56,
                borderRadius: '16px 16px 0 0',
                bgcolor: 'primary.main',
                color: 'white',
                boxShadow: '0 -2px 12px rgba(37, 99, 235, 0.4)',
                '&:hover': {
                  bgcolor: 'primary.dark',
                  boxShadow: '0 -4px 16px rgba(37, 99, 235, 0.45)',
                },
              }}
              aria-label="Mở trợ lý AI"
            >
              <ChatIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      <Dialog open={!!viewLawId} onClose={() => setViewLawId(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Văn bản QPPL</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {ALL_LAW_RESULTS.find((r) => r.id === viewLawId)?.full ?? ''}
          </Typography>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewCaseId} onClose={() => setViewCaseId(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Án lệ</DialogTitle>
        <DialogContent>
          {(() => {
            const c = ALL_CASE_LAW.find((r) => r.id === viewCaseId)
            return c ? (
              <>
                <Typography variant="body2" fontWeight={600}>{c.code}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{c.summary}</Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>Kết quả: {c.result}</Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>{c.full}</Typography>
              </>
            ) : null
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={!!compareCaseId} onClose={() => setCompareCaseId(null)} maxWidth="sm" fullWidth>
        <DialogTitle>So sánh với vụ án hiện tại</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {ALL_CASE_LAW.find((c) => c.id === compareCaseId)?.code ?? ''} – So sánh điểm tương đồng và khác biệt (nội dung demo; tích hợp thật sẽ do AI phân tích).
          </Typography>
        </DialogContent>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack('')} message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  )
}

export default AIResearchPage
